/**
 * `/$org/apps`: every app on one page, the ones in use as tiles and the rest
 * to add below. `?category=` narrows the apps to add to one category.
 *
 * @module routes/$org.apps.index
 */
import { createFileRoute, redirect } from "@tanstack/react-router";

import { AppsFrame } from "../components/apps/AppsFrame";
import { AppsPage } from "../components/apps/AppsPage";
import { isAppCategory, type AppCategory } from "../components/apps/appRegistry";
import { useRegulationOrg } from "../components/regulation/useRegulationOrg";

export interface AppsSearch {
  readonly category?: AppCategory;
}

function AppsRouteView() {
  const { org: orgSegment } = Route.useParams();
  const { category } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { org } = useRegulationOrg();
  return (
    <AppsFrame>
      <AppsPage
        organizationId={org?.id ?? null}
        orgSegment={orgSegment}
        orgName={org?.name ?? null}
        category={category ?? null}
        onCategoryChange={(next) =>
          void navigate({ search: next === null ? {} : { category: next }, replace: true })
        }
      />
    </AppsFrame>
  );
}

export const Route = createFileRoute("/$org/apps/")({
  beforeLoad: async ({ context }) => {
    if (
      context.authGateState.status !== "authenticated" &&
      context.authGateState.status !== "hosted-static"
    ) {
      throw redirect({ to: "/pair", replace: true });
    }
  },
  validateSearch: (search: Record<string, unknown>): AppsSearch =>
    isAppCategory(search.category) ? { category: search.category } : {},
  component: AppsRouteView,
});
